import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import QRCodeLib from 'qrcode'
import { Download, Copy, Share2, Check, ExternalLink } from 'lucide-react'

export default function QRCode() {
  const { currentUser } = useAuth()
  const [vcard, setVcard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [copied, setCopied] = useState(false)
  const canvasRef = useRef(null)

  const vcardUrl = vcard?.username 
    ? `${window.location.origin}/v/${vcard.username}` 
    : ''

  useEffect(() => {
    async function fetchVCard() {
      if (!currentUser) return

      try {
        const vcardDoc = await getDoc(doc(db, 'vcards', currentUser.uid))
        if (vcardDoc.exists()) {
          setVcard(vcardDoc.data())
        }
      } catch (error) {
        console.error('Error fetching vCard:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchVCard()
  }, [currentUser])

  useEffect(() => {
    async function generateQR() {
      if (!vcardUrl) return

      try {
        const dataUrl = await QRCodeLib.toDataURL(vcardUrl, {
          width: 400,
          margin: 2,
          color: {
            dark: '#0A1B2A',
            light: '#FFFFFF'
          }
        })
        setQrDataUrl(dataUrl)
      } catch (error) {
        console.error('Error generating QR:', error)
      }
    }

    generateQR()
  }, [vcardUrl])

  async function downloadQR(format) {
    if (!vcardUrl) return

    if (format === 'png') {
      const link = document.createElement('a')
      link.download = `vcard-${vcard.username}.png`
      link.href = qrDataUrl
      link.click()
    } else if (format === 'svg') {
      const svg = await QRCodeLib.toString(vcardUrl, {
        type: 'svg',
        width: 400,
        margin: 2,
        color: {
          dark: '#0A1B2A',
          light: '#FFFFFF'
        }
      })
      
      const blob = new Blob([svg], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = `vcard-${vcard.username}.svg`
      link.href = url
      link.click()
      URL.revokeObjectURL(url)
    }
  }

  function copyLink() {
    navigator.clipboard.writeText(vcardUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function shareLink() {
    if (navigator.share) {
      navigator.share({
        title: `${vcard?.name}'s Digital vCard`,
        text: 'Check out my digital business card!',
        url: vcardUrl
      })
    } else {
      copyLink()
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '50vh' }}>
        <div className="spinner"></div>
      </div>
    )
  }

  if (!vcard) {
    return (
      <div className="empty-state">
        <div className="empty-icon">
          <Download size={40} />
        </div>
        <h3>No vCard Created</h3>
        <p>Create your vCard first to get your QR code</p>
      </div>
    )
  }

  return (
    <div className="qr-page">
      <div className="page-header">
        <h1 className="page-title">Your QR Code</h1>
        <p className="page-subtitle">Download and share your vCard QR code</p>
      </div>

      <div className="qr-layout">
        <div className="qr-preview-card dashboard-card">
          <div className="qr-preview">
            {qrDataUrl && (
              <img src={qrDataUrl} alt="QR Code" className="qr-image" />
            )}
          </div>
          <p className="qr-label">Scan to view your vCard</p>
          
          <div className="qr-download-buttons">
            <button className="btn btn-primary" onClick={() => downloadQR('png')}>
              <Download size={18} />
              Download PNG
            </button>
            <button className="btn btn-secondary" onClick={() => downloadQR('svg')}>
              <Download size={18} />
              Download SVG
            </button>
          </div>
        </div>

        <div className="qr-share-card dashboard-card">
          <h3 className="dashboard-card-title">Share Your vCard</h3>
          
          <div className="share-link-box">
            <input 
              type="text" 
              className="form-input" 
              value={vcardUrl} 
              readOnly 
            />
            <button 
              className="btn btn-primary btn-icon" 
              onClick={copyLink}
              title="Copy link"
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
            </button>
          </div>

          <a 
            href={vcardUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn btn-ghost"
            style={{ width: '100%', marginTop: 'var(--space-md)' }}
          >
            <ExternalLink size={18} />
            View Live vCard
          </a>

          <div className="divider"></div>

          <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-md)' }}>
            Share via
          </h4>

          <div className="share-buttons">
            <a 
              href={`https://wa.me/?text=${encodeURIComponent(`Check out my digital vCard: ${vcardUrl}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="share-btn whatsapp"
            >
              WhatsApp
            </a>
            <a 
              href={`mailto:?subject=My Digital vCard&body=${encodeURIComponent(`Check out my digital vCard: ${vcardUrl}`)}`}
              className="share-btn email"
            >
              Email
            </a>
            <button onClick={shareLink} className="share-btn share">
              <Share2 size={16} />
              Share
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .qr-layout {
          display: grid;
          grid-template-columns: 400px 1fr;
          gap: var(--space-lg);
          align-items: start;
        }
        
        @media (max-width: 900px) {
          .qr-layout {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .qr-download-buttons {
            flex-direction: column;
          }
          
          .qr-preview {
            padding: var(--space-md);
          }
          
          .qr-image {
            width: 100%;
            max-width: 200px;
            height: auto;
          }
          
          .qr-layout {
            gap: var(--space-md);
          }
          
          .share-btn {
            flex: 100%;
          }
        }
        
        .qr-preview-card {
          text-align: center;
        }
        
        .qr-preview {
          background: white;
          padding: var(--space-lg);
          border-radius: var(--radius-lg);
          border: 2px dashed var(--gray-200);
          margin-bottom: var(--space-md);
        }
        
        .qr-image {
          width: 250px;
          height: 250px;
          margin: 0 auto;
        }
        
        .qr-label {
          font-size: var(--font-size-sm);
          color: var(--text-muted);
          margin-bottom: var(--space-lg);
        }
        
        .qr-download-buttons {
          display: flex;
          gap: var(--space-sm);
        }
        
        .qr-download-buttons .btn {
          flex: 1;
        }
        
        .share-link-box {
          display: flex;
          gap: var(--space-sm);
        }
        
        .share-link-box input {
          flex: 1;
          background: var(--gray-50);
        }
        
        .share-buttons {
          display: flex;
          gap: var(--space-sm);
          flex-wrap: wrap;
        }
        
        .share-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: var(--space-sm);
          padding: 10px 16px;
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          font-weight: 500;
          flex: 1;
          min-width: 100px;
          border: none;
          cursor: pointer;
          transition: opacity var(--transition-fast);
        }
        
        .share-btn:hover {
          opacity: 0.9;
        }
        
        .share-btn.whatsapp {
          background: #25D366;
          color: white;
        }
        
        .share-btn.email {
          background: var(--gray-700);
          color: white;
        }
        
        .share-btn.share {
          background: var(--primary);
          color: white;
        }
      `}</style>
    </div>
  )
}
